/**
 * The app's token root (1.I-1 deliverable 2): Grimoire Noir on every page through `cx-root`,
 * with the portal container, the Esc-owning `LayerProvider`, notices and the open seam; then the
 * frame or the bare page. The design page (its own token roots) and the capture page (1.H's
 * literal colors) stay outside the token root: their providers sit on a plain `div`.
 */
import {
  DEFAULT_DENSITY,
  DEFAULT_DIRECTION,
  DirectionProvider,
  LayerProvider,
  PortalContainer,
} from '@codex/ui';
import { Outlet, useLocation } from '@tanstack/react-router';
import { type ComponentType, type ReactNode, useState } from 'react';
import { Frame } from './frame.js';
import { isFramelessPath, showFrame } from './frame-rule.js';
import { NoticeProvider } from './notices.js';
import { DefaultOpenDocProvider } from './open-doc.js';
import { useShellSession } from './session.js';
import './shell.css';

/** The slot `routes/__root.tsx` fills (1.I-2's open modes): inside the notices, the layer rule and the token root. */
export type FrameWrapper = ComponentType<{ children: ReactNode }>;

function PassThrough({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

/** Notices, the default open handler, then the wrapper, around the frame or the bare page. */
export function AppProviders({
  Wrapper = PassThrough,
  children,
}: {
  Wrapper?: FrameWrapper;
  children?: ReactNode;
}) {
  return (
    <NoticeProvider>
      <DefaultOpenDocProvider>
        <Wrapper>{children}</Wrapper>
      </DefaultOpenDocProvider>
    </NoticeProvider>
  );
}

export function AppRoot({
  Wrapper,
  Content = PassThrough,
}: {
  Wrapper?: FrameWrapper;
  /** Wraps the routed page inside the frame (1.I-2's split pane and palette). */
  Content?: FrameWrapper;
} = {}) {
  const pathname = useLocation({ select: (location) => location.pathname });
  const me = useShellSession();
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const rooted = !isFramelessPath(pathname);
  const framed = showFrame({ me, pathname });
  const rootProps = rooted
    ? {
        className: 'cx-root cx-app-root',
        'data-direction': DEFAULT_DIRECTION,
        'data-mode': 'system',
        'data-density': DEFAULT_DENSITY,
        'data-motion': 'system',
      }
    : {};
  return (
    <div ref={setRoot} {...rootProps}>
      <DirectionProvider direction={DEFAULT_DIRECTION}>
        <PortalContainer element={root}>
          <LayerProvider>
            <AppProviders {...(Wrapper ? { Wrapper } : {})}>
              {framed ? (
                <Frame>
                  <Content>
                    <Outlet />
                  </Content>
                </Frame>
              ) : (
                <Outlet />
              )}
            </AppProviders>
          </LayerProvider>
        </PortalContainer>
      </DirectionProvider>
    </div>
  );
}
